import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { UploadCloud } from "lucide-react";

/**
 * CloudinaryUploader — opens the official Cloudinary upload widget.
 *
 * Props:
 *  - mode: "admin" | "public" — which API namespace to call for config/sign
 *  - folder: subfolder in Cloudinary
 *  - onUploaded: (url, info) => void — called for each successful upload
 *  - multiple: bool
 *  - label: button text
 */
export function CloudinaryUploader({ mode = "admin", folder = "sustainably_curated", onUploaded, multiple = true, label = "Upload image" }) {
  const widgetRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const base = mode === "admin" ? "/admin/uploads" : "/public/uploads";
    api.get(`${base}/cloudinary-config`, { params: { folder } })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ configured: false }));
  }, [mode, folder]);

  function open() {
    if (!config?.configured) return;
    if (!window.cloudinary) {
      // wait briefly for the CDN script
      setLoading(true);
      const start = Date.now();
      const wait = setInterval(() => {
        if (window.cloudinary) { clearInterval(wait); setLoading(false); open(); }
        else if (Date.now() - start > 6000) { clearInterval(wait); setLoading(false); }
      }, 100);
      return;
    }
    if (!widgetRef.current) {
      widgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: config.cloud_name,
          apiKey: config.api_key,
          uploadSignature: (callback, paramsToSign) => {
            const base = mode === "admin" ? "/admin/uploads" : "/public/uploads";
            api.post(`${base}/cloudinary-sign`, { params_to_sign: paramsToSign })
              .then((r) => callback(r.data.signature))
              .catch(() => callback(""));
          },
          folder,
          multiple,
          sources: ["local", "url", "camera"],
          maxFileSize: 8_000_000,
          clientAllowedFormats: ["png", "jpg", "jpeg", "webp", "heic"],
          styles: { palette: { window: "#FDFBF7", windowBorder: "#1B4332", tabIcon: "#1B4332", link: "#1B4332" } },
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            onUploaded && onUploaded(result.info.secure_url, result.info);
          }
        }
      );
    }
    widgetRef.current.open();
  }

  if (!config) return <button disabled className="text-xs text-[#6C757D]">Checking…</button>;
  if (!config.configured) {
    return (
      <div className="text-xs text-[#6C757D] border border-dashed border-[#E9ECEF] rounded p-2">
        Cloudinary not configured. Set credentials in backend .env to enable image uploads.
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="inline-flex items-center gap-1.5 bg-white border border-[#E9ECEF] text-[#212529] rounded-md px-3 py-1.5 text-sm hover:bg-[#F1F3F5]"
      data-testid="cloudinary-upload-button"
    >
      <UploadCloud className="w-4 h-4" />
      {loading ? "Loading widget…" : label}
    </button>
  );
}
